import React, { useEffect, useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { LogOut, X, RefreshCw, Mailbox } from 'lucide-react';
import { Button } from './ui/button';
import useThemeStore from '../store/themeStore';
import axiosInstance from '../api/axios';
import '../styles/adminUXFixes.css';
import { ErrorBoundary } from './ErrorBoundary';

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const user = useAuthStore(state => state.user);
  const isHydrated = useAuthStore(state => state.isHydrated);
  const logout = useAuthStore(state => state.logout);
  const updateActivity = useAuthStore(state => state.updateActivity);
  const checkSessionExpiry = useAuthStore(state => state.checkSessionExpiry);
  const forceHydrate = useAuthStore(state => state.forceHydrate);
  const branding = useThemeStore(state => state.branding);
  const activeTheme = useThemeStore(state => state.activeTheme);
  const [inboxUnread, setInboxUnread] = useState(0);

  // Poll unread inbox count every 60s for the badge
  useEffect(() => {
    if (!isAuthenticated) return;
    let stopped = false;
    const load = async () => {
      try {
        const res = await axiosInstance.get('/admin-inbox/unread-count');
        if (!stopped) setInboxUnread(res.data?.unread_count || 0);
      } catch (_) { /* silent */ }
    };
    load();
    const id = setInterval(load, 60000);
    return () => { stopped = true; clearInterval(id); };
  }, [isAuthenticated, location.pathname]);
  
  // Check if we're on the dashboard (main admin page)
  const isOnDashboard = location.pathname === '/admin' || location.pathname === '/admin/';

  // Switch to admin manifest for PWA
  useEffect(() => {
    // Update manifest link for admin PWA
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      manifestLink.href = '/admin-manifest.json';
    }
    
    // Update apple-touch-icon for iOS
    const appleIcons = document.querySelectorAll('link[rel="apple-touch-icon"]');
    appleIcons.forEach(icon => {
      icon.href = '/admin-icon-192.png';
    });
    
    // Update apple-mobile-web-app-title
    let titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (titleMeta) {
      titleMeta.content = 'HYS Admin';
    }
    
    // Update theme color
    let themeMeta = document.querySelector('meta[name="theme-color"]');
    if (themeMeta) {
      themeMeta.content = '#5D4037';
    }
    
    // Cleanup: restore original manifest when leaving admin
    return () => {
      if (manifestLink) {
        manifestLink.href = '/manifest.json';
      }
      appleIcons.forEach(icon => {
        icon.href = '/app-icon-192.png';
      });
      if (titleMeta) {
        titleMeta.content = 'HYS';
      }
      if (themeMeta) {
        themeMeta.content = '#C4A084';
      }
    };
  }, []);

  // Force hydrate on mount to ensure we have the latest state from localStorage
  useEffect(() => {
    console.log('[ADMIN] AdminLayout mounted, forcing hydration');
    
    // Double-check localStorage directly on mobile
    try {
      const storedAuth = localStorage.getItem('auth-storage');
      console.log('[ADMIN] Direct localStorage check:', !!storedAuth);
      if (storedAuth) {
        const parsed = JSON.parse(storedAuth);
        console.log('[ADMIN] Stored user role:', parsed?.state?.user?.role);
      }
    } catch (e) {
      console.error('[ADMIN] localStorage check failed:', e);
    }
    
    forceHydrate();
  }, [forceHydrate]);

  // Auth check after hydration is complete
  useEffect(() => {
    // Don't check until hydrated
    if (!isHydrated) {
      console.log('[ADMIN] Waiting for hydration...');
      return;
    }

    // Additional direct localStorage check for mobile Safari
    let effectiveUser = user;
    let effectiveIsAuth = isAuthenticated;
    
    if (!isAuthenticated) {
      console.log('[ADMIN] Not authenticated via store, checking localStorage directly...');
      try {
        const storedAuth = localStorage.getItem('auth-storage');
        if (storedAuth) {
          const parsed = JSON.parse(storedAuth);
          console.log('[ADMIN] localStorage parsed:', { hasUser: !!parsed?.state?.user, role: parsed?.state?.user?.role });
          if (parsed?.state?.user && parsed?.state?.token) {
            console.log('[ADMIN] Recovering auth from localStorage');
            effectiveUser = parsed.state.user;
            effectiveIsAuth = true;
            
            // Restore Zustand state from localStorage
            if (parsed.state.user.role === 'admin') {
              console.log('[ADMIN] Force restoring admin auth to Zustand store');
              useAuthStore.getState().setAuth(parsed.state.user, parsed.state.token);
              return; // Will re-run effect with new state
            }
          }
        }
        
        // Also check cookies
        const cookieUser = document.cookie.split('; ').find(row => row.startsWith('auth-user='));
        const cookieToken = document.cookie.split('; ').find(row => row.startsWith('auth-token='));
        
        if (cookieUser && cookieToken) {
          try {
            const userValue = decodeURIComponent(cookieUser.split('=')[1]);
            const tokenValue = decodeURIComponent(cookieToken.split('=')[1]);
            const parsedUser = JSON.parse(userValue);
            console.log('[ADMIN] Found cookies:', { email: parsedUser?.email, role: parsedUser?.role });
            
            if (parsedUser?.role === 'admin') {
              console.log('[ADMIN] Recovering admin auth from cookies');
              effectiveUser = parsedUser;
              effectiveIsAuth = true;
              useAuthStore.getState().setAuth(parsedUser, tokenValue);
              return; // Will re-run effect
            }
          } catch (e) {
            console.error('[ADMIN] Cookie parsing failed:', e);
          }
        }
      } catch (e) {
        console.error('[ADMIN] localStorage/cookie recovery failed:', e);
      }
    }

    console.log('[ADMIN] Auth check - isAuthenticated:', effectiveIsAuth, 'user:', effectiveUser?.email, 'role:', effectiveUser?.role);

    // Check if session expired
    const expired = checkSessionExpiry();
    if (expired) {
      console.log('[ADMIN] Session expired, redirecting to admin-login');
      window.location.replace('/admin-login.html');
      return;
    }

    // If not authenticated, redirect to admin-login
    if (!effectiveIsAuth) {
      console.log('[ADMIN] Not authenticated, redirecting to admin-login');
      window.location.replace('/admin-login.html');
      return;
    }

    // Check if user has admin role
    if (effectiveUser && effectiveUser.role !== 'admin') {
      console.log('[ADMIN] User is not admin, redirecting to homepage');
      window.location.replace('/');
    }
  }, [isAuthenticated, user, navigate, checkSessionExpiry, isHydrated]);

  // Auto-logout after 20 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const TIMEOUT_DURATION = 20 * 60 * 1000; // 20 minutes in milliseconds
    let timeoutId;

    const resetTimer = () => {
      // Update activity timestamp
      updateActivity();

      // Clear existing timeout
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      // Set new timeout
      timeoutId = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logout();
        navigate('/login?redirect=/admin&reason=timeout', { replace: true });
      }, TIMEOUT_DURATION);
    };

    // Events that count as user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer);
    });

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      events.forEach(event => {
        document.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated, logout, navigate, updateActivity]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Force complete app refresh — clears all caches & service workers
  const handleForceRefresh = async () => {
    try {
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      try {
        localStorage.removeItem('app-version');
        localStorage.removeItem('icon-version');
      } catch (_) {}
    } catch (_) {}
    // Hard reload with cache bypass
    window.location.href = window.location.pathname + '?v=' + Date.now();
  };

  // Show loading state while hydrating
  if (!isHydrated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700">Lädt...</p>
        </div>
      </div>
    );
  }

  // Don't render anything if not authenticated (will redirect)
  if (!isAuthenticated || !user) {
    // Double-check localStorage before showing redirect message
    try {
      const stored = localStorage.getItem('auth-storage');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.state?.user?.role === 'admin' && parsed?.state?.token) {
          // We have valid auth in storage, force hydrate
          console.log('[ADMIN] Found valid auth in localStorage, triggering hydrate...');
          forceHydrate();
        }
      }
    } catch (e) {
      // Ignore
    }
    
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-700">Authentifizierung wird geprüft...</p>
        </div>
      </div>
    );
  }

  // Don't render if not admin (will redirect)
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#E8E0D5' }}>
        <div className="text-center">
          <p className="text-gray-700">Keine Berechtigung...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col admin-mobile-scroll">
      {/* Admin Header - sticky on mobile so navigation is always reachable */}
      <header 
        className="border-b border-gray-200 sticky top-0 z-40"
        style={{ backgroundColor: '#AF9774' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              {/* Admin Logo */}
              <button 
                onClick={() => navigate('/admin')}
                className="hover:opacity-80 transition-opacity"
                title="Zum Admin-Dashboard"
              >
                <img 
                  src="/admin-logo.png" 
                  alt="Admin Logo" 
                  className="h-10 sm:h-12 w-auto"
                />
              </button>
              <button
                onClick={() => navigate('/admin')}
                className="hover:opacity-90 transition-opacity hidden sm:block"
                title="Zum Admin-Dashboard"
              >
                <div>
                  <h1 className="text-lg sm:text-xl font-bold text-white">{branding.shopName} Admin</h1>
                  <p className="text-xs text-white/80">Verwaltungsbereich</p>
                </div>
              </button>
            </div>
            
            <div className="flex items-center gap-2 md:gap-4">
              {/* X Button - Show on all pages except dashboard */}
              {!isOnDashboard && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="text-white hover:bg-white/20"
                  title="Zurück zum Dashboard"
                >
                  <X className="h-5 w-5" />
                </Button>
              )}

              {/* Postfach button with unread badge */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/admin/postfach')}
                className="text-white hover:bg-white/20 relative"
                title={inboxUnread > 0 ? `${inboxUnread} ungelesene Nachricht${inboxUnread === 1 ? '' : 'en'}` : 'Postfach'}
                data-testid="admin-inbox-btn"
              >
                <Mailbox className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Postfach</span>
                {inboxUnread > 0 && (
                  <span
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full bg-red-500 text-white px-1"
                    data-testid="admin-inbox-badge"
                  >
                    {inboxUnread > 99 ? '99+' : inboxUnread}
                  </span>
                )}
              </Button>

              {/* Force-refresh button — fixes stale PWA cache */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleForceRefresh}
                className="text-white hover:bg-white/20"
                title="App aktualisieren (Cache leeren)"
                data-testid="admin-force-refresh-btn"
              >
                <RefreshCw className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Aktualisieren</span>
              </Button>

              <span className="text-xs md:text-sm text-white hidden sm:block">
                <strong>{user?.email}</strong>
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-white hover:bg-white/20"
              >
                <LogOut className="h-4 w-4 md:mr-2" />
                <span className="hidden md:inline">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Admin Content */}
      <main className="flex-1">
        <ErrorBoundary homeHref="/admin">
          <Outlet />
        </ErrorBoundary>
      </main>

      {/* Admin Footer */}
      <footer className="border-t py-4" style={{ backgroundColor: '#AF9774' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-white">
            © {new Date().getFullYear()} {branding.shopName} Admin Panel
          </p>
        </div>
      </footer>
    </div>
  );
};

export default AdminLayout;
